// Hub location selector for the Current Staff Plan top card. Lets the user
// pick from the model's defined OGS hubs only. The shore type travels with
// the selection so reports can group hub FTE correctly.

import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import clsx from 'clsx';
import type { Model, StaffPlanHubLocation } from '@rc/engine/types';

interface Props {
  model: Model;
  value: StaffPlanHubLocation | undefined;
  onChange: (next: StaffPlanHubLocation | undefined) => void;
}

const NONE = '__none__';

export default function HubLocationPicker({ model, value, onChange }: Props) {
  const hubs = useMemo(() => model.ogsHubs ?? [], [model.ogsHubs]);

  const selectValue = value?.hubId ?? NONE;

  const handleSelectChange = (next: string) => {
    if (next === NONE) {
      onChange(undefined);
      return;
    }
    const hub = hubs.find((h) => h.id === next);
    if (!hub) return;
    onChange({ hubId: hub.id, label: hub.label, shore: hub.shore });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <MapPin size={14} className="text-brand-purple-500" />
        <label className="label !mb-0">Default hub location</label>
        {value && (
          <span
            className={clsx(
              'pill text-[10px]',
              value.shore === 'nearshore'
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-800',
            )}
          >
            {value.shore}
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          className="input text-sm sm:max-w-[280px]"
          value={selectValue}
          onChange={(e) => handleSelectChange(e.target.value)}
          disabled={hubs.length === 0}
        >
          <option value={NONE}>— No hub specified —</option>
          {hubs.map((h) => (
            <option key={h.id} value={h.id}>
              {h.label} ({h.shore})
            </option>
          ))}
        </select>
      </div>
      <p className="text-[11px] text-slate-400">
        {hubs.length === 0
          ? 'This model has no hubs defined — add hubs in the Model editor to enable hub locations.'
          : 'Default location for Hub roles across the plan. Individual Discipline × Channel rows can override this in the grid below; rows left on "Default" use this location.'}
      </p>
    </div>
  );
}
